import { BrainwaveSettings } from '../hooks/useWebAudio';
import { NoiseSettings } from '../utils/audio/WhiteNoiseGeneratorNode';
import { scriptService } from './scriptService';

export interface SerializableAudioTrack {
  id: string;
  name: string;
  type: 'main' | 'music' | 'background' | 'sound' | 'brainwave' | 'noise';
  audioLibraryId?: string; // Reference to reload from audio library
  audioFilePath?: string; // Path to the audio file (for library tracks)
  volume: number;
  muted: boolean;
  startTime: number;
  duration: number;
  trimmedDuration?: number;
  color: string;
  fadeIn?: number;
  fadeOut?: number;
  brainwaveSettings?: BrainwaveSettings; // Contains controlPoints and modulationType
  noiseSettings?: NoiseSettings; // Contains noise type and frequency bands
}

export interface AudioMixData {
  version: string; // For future compatibility
  masterVolume: number;
  totalDuration: number;
  tracks: SerializableAudioTrack[];
  createdAt: string;
  updatedAt: string;
}

class AudioMixService {

  /**
   * Save audio mix data for a script (now delegated to scriptService)
   */
  async saveMixData(scriptId: string, mixData: Omit<AudioMixData, 'createdAt' | 'updatedAt'> | AudioMixData): Promise<void> {
    try {
      // Add timestamps - preserve existing createdAt if it exists, otherwise create new one
      const fullMixData: AudioMixData = {
        ...mixData,
        createdAt: 'createdAt' in mixData && mixData.createdAt ? mixData.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await scriptService.saveAudioMixData(scriptId, fullMixData);
      console.log('Audio mix saved successfully');
    } catch (error) {
      console.error('Error saving audio mix:', error);
      throw error;
    }
  }

  /**
   * Load audio mix data for a script (now delegated to scriptService)
   */
  async loadMixData(scriptId: string): Promise<AudioMixData | null> {
    try {
      const mixData = await scriptService.loadAudioMixData(scriptId);
      
      if (mixData) {
        console.log('Audio mix loaded successfully');
        return mixData;
      } else {
        // No saved mix data found
        return null;
      }
    } catch (error) {
      console.error('Error loading audio mix:', error);
      throw error;
    }
  }

  /**
   * Delete audio mix data for a script (now delegated to scriptService)
   */
  async deleteMixData(scriptId: string): Promise<void> {
    try {
      await scriptService.deleteAudioMixData(scriptId);
      console.log('Audio mix deleted successfully');
    } catch (error) {
      console.error('Error deleting audio mix:', error);
      throw error;
    }
  }

  /**
   * Validate audio mix data structure
   */
  validateMixData(mixData: any): mixData is AudioMixData {
    if (!mixData || typeof mixData !== 'object') {
      return false;
    }

    const required = ['version', 'masterVolume', 'totalDuration', 'tracks'];
    for (const field of required) {
      if (!(field in mixData)) {
        console.warn(`Missing required field: ${field}`);
        return false;
      }
    }

    if (!Array.isArray(mixData.tracks)) {
      console.warn('Tracks must be an array');
      return false;
    }

    // Validate each track
    for (const track of mixData.tracks) {
      if (!this.validateTrackData(track)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Validate individual track data
   */
  private validateTrackData(track: any): track is SerializableAudioTrack {
    if (!track || typeof track !== 'object') {
      return false;
    }

    const required = ['id', 'name', 'type', 'volume', 'muted', 'startTime', 'duration', 'color'];
    for (const field of required) {
      if (!(field in track)) {
        console.warn(`Missing required track field: ${field}`);
        return false;
      }
    }

    // Validate track type
    const validTypes = ['main', 'music', 'background', 'sound', 'brainwave', 'noise'];
    if (!validTypes.includes(track.type)) {
      console.warn(`Invalid track type: ${track.type}`);
      return false;
    }

    // Validate brainwave settings if present
    if (track.type === 'brainwave' && track.brainwaveSettings) {
      if (!this.validateBrainwaveSettings(track.brainwaveSettings)) {
        return false;
      }
    }

    // Validate noise settings if present
    if (track.type === 'noise' && track.noiseSettings) {
      if (!this.validateNoiseSettings(track.noiseSettings)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Validate brainwave settings structure
   */
  private validateBrainwaveSettings(settings: any): settings is BrainwaveSettings {
    if (!settings || typeof settings !== 'object') {
      return false;
    }

    if (!settings.modulationType || !settings.controlPoints) {
      console.warn('Invalid brainwave settings structure');
      return false;
    }

    const validModulations = ['binaural', 'amplitude', 'frequency', 'stereo', 'isochronic'];
    if (!validModulations.includes(settings.modulationType)) {
      console.warn(`Invalid modulation type: ${settings.modulationType}`);
      return false;
    }

    if (!Array.isArray(settings.controlPoints)) {
      console.warn('Control points must be an array');
      return false;
    }

    // Validate control points
    for (const point of settings.controlPoints) {
      if (!point || typeof point !== 'object') {
        return false;
      }
      const required = ['time', 'carrierFreq', 'brainwaveFreq', 'amplitude'];
      for (const field of required) {
        if (!(field in point) || typeof point[field] !== 'number') {
          console.warn(`Invalid control point field: ${field}`);
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Validate noise settings structure
   */
  private validateNoiseSettings(settings: any): settings is NoiseSettings {
    if (!settings || typeof settings !== 'object') {
      return false;
    }

    // Check required fields
    const required = ['noiseType', 'frequencyBands', 'volume', 'duration'];
    for (const field of required) {
      if (!(field in settings)) {
        console.warn(`Missing required noise settings field: ${field}`);
        return false;
      }
    }

    // Validate noise type
    const validNoiseTypes = ['white', 'pink', 'brown'];
    if (!validNoiseTypes.includes(settings.noiseType)) {
      console.warn(`Invalid noise type: ${settings.noiseType}`);
      return false;
    }

    // Validate frequency bands
    if (!Array.isArray(settings.frequencyBands)) {
      console.warn('Frequency bands must be an array');
      return false;
    }

    // Validate each frequency band
    for (const band of settings.frequencyBands) {
      if (!band || typeof band !== 'object') {
        return false;
      }
      const bandRequired = ['name', 'minFreq', 'maxFreq', 'gain'];
      for (const field of bandRequired) {
        if (!(field in band)) {
          console.warn(`Invalid frequency band field: ${field}`);
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Check if mix data is compatible with current version
   */
  isCompatibleVersion(mixData: AudioMixData): boolean {
    const currentVersion = '1.0.0';
    const mixVersion = mixData.version;

    // For now, only accept exact version match
    // In the future, implement version migration logic
    return mixVersion === currentVersion;
  }

  /**
   * Create a default empty mix data structure
   */
  createEmptyMixData(): AudioMixData {
    return {
      version: '1.0.0',
      masterVolume: 1.0,
      totalDuration: 0,
      tracks: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  }
}

export const audioMixService = new AudioMixService();
export default audioMixService;